import React, {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {Redirect, useLocation, useParams} from 'react-router-dom';
import {Heading, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {canOpenReportBuilder} from './reportBuilderGate';
import {BuilderRole, ReportBuilderProvider, ReportIntentPayload} from './ReportBuilderContext';
import {ReportBuilderSurface} from './ReportBuilderSurface';
import {UploadingOverlay} from '../../forecast-app/shared/components/uploading-overlay/UploadingOverlay';
import {validateReportDefinition} from './contracts/validator';
import {
	isReportDataAccessDenied,
	LoadedReport,
	ReportDataFetchError,
	ReportDataSource,
	ReportRefusalReason,
} from './data/ReportDataSource';
import {HttpReportDataSource} from './data/HttpReportDataSource';
import type {ReportTaxonomy} from './contracts/report-taxonomy.types';
import {useReportTaxonomy} from './useReportTaxonomy';
import {grainOf} from './reportGrain';
import {SavedReportRef} from './data/savedReportInput';
import {TestHook} from './TestHook';
import {useAgentDrawerContextOptional} from '../../forecast-app/shared/contexts/AgentDrawerContext';
import {useTrackPage} from '../../tracking/amplitude/hooks/useTrackPage';

/** Production data source: the real `POST /report/data/fetch` transport. A module singleton so a
 * re-render does not churn a new instance (keeps the provider's memoised source stable).
 * Tests/Storybook inject a `MockReportDataSource` via the `dataSource` prop instead. */
const httpReportDataSource: ReportDataSource = new HttpReportDataSource();

/** Report intent handed to the page through react-router location state when the chat card's "View"
 * navigates here. The intent is ephemeral (agent-generated, not persisted), so a refresh or direct
 * hit carries no state and the page redirects away. */
interface ReportBuilderLocationState {
	initialConfig?: ReportIntentPayload;
	/** Logged-in person id, scopes the per-user layout overlay. Falls back to the provider's 'me'. */
	userId?: string;
	/** Taxonomy the chat surface fetched when the intent arrived. Optional, and used only when it
	 * is already the grain being shown — the view resolves its own otherwise. Absent on the
	 * saved-report path, which has no router state to carry one. */
	taxonomy?: ReportTaxonomy;
	/** Id of the chat message whose card opened this draft. Carried so the first save can tell that
	 * card the report now exists, after which its View opens the stored report rather than rebuilding
	 * a draft from the agent's original — and by then stale — payload. */
	intentMessageId?: string;
}

/** Fills the routed content area as a normal page (in-flow, not a portalled full-viewport takeover).
 * The surface itself scrolls. */
const Surface = styled.div`
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: auto;
`;

const tokens = tokensForThemeProvider.sky.sys;

/** A column, not a plain block: the design-system `Text` renders inline-block, so two of them in a
 * block ran together on one line and read as a single run-on sentence. */
const ErrorPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.spacing['200']};
	padding: 24px;
`;

/** The name this page reports to Amplitude. `useTrackPage` both fires the Page Viewed event under it
 * and registers it as the `pageName` super property, so every event fired while the builder is open -
 * the report table's task modal, for one - is attributable to this page rather than arriving with no
 * page name at all. Proper Case is required: `TrackingV2.track` rejects any other event name. */
export const REPORT_BUILDER_PAGE_NAME = 'AI Report Builder';

/** Wraps the page surface so a page view is registered for every mount that renders the page, and
 * never for one that just redirects away.
 *
 * It deliberately sits ABOVE `SavedReportBuilder`'s load and validation branches, so re-opening a
 * report whose stored definition fails to load or fails validation still counts as a view: the reader
 * did navigate here, and the events they fire on that error screen need the page name just as much.
 * The chat-intent branch below reports no view for its own invalid-definition panel, because that
 * guard returns before this wrapper is reached.
 *
 * Keyed by report at both call sites - the stored report id when the URL carries one, the draft id
 * when the report exists only in router state - so opening a second report in the same session is a
 * second page view rather than one view that silently covers every report the user reads. Without the
 * draft id, a second chat card's View would swap the report on screen under the first report's page
 * view. `savedReportId` rides the event (not the super property) so the views are attributable per
 * report without stamping a report id onto every unrelated event fired while the builder is open.
 *
 * The re-fire is done by remounting rather than by passing `useTrackPage` a deps array:
 * the hook registers during render but unregisters from a ref in an effect cleanup, so on a deps
 * change the cleanup would run *after* the re-registration and clear the checksum it had just
 * written. A fresh mount gets a fresh ref, and the outgoing unmount's checksum no longer matches,
 * so its unregister correctly no-ops. */
function TrackedSurface({children, reportId}: {children: React.ReactNode; reportId?: string}): JSX.Element {
	useTrackPage(REPORT_BUILDER_PAGE_NAME, {savedReportId: reportId ?? null});
	return <Surface data-cy="ai-report-builder-page">{children}</Surface>;
}

export interface ReportBuilderPageProps {
	/** Overridable for tests/Storybook; production defaults to the live `HttpReportDataSource`. */
	dataSource?: ReportDataSource;
}

interface ReportBuilderViewProps {
	initialConfig: ReportIntentPayload;
	userId?: string;
	/** Taxonomy the chat surface may have passed in router state; used only to seed the fetch so
	 * editing works instantly when present. The view fetches its own regardless. */
	taxonomySeed?: ReportTaxonomy;
	dataSource: ReportDataSource;
	/** The Reports-list entry this report already exists as, when opened on a saved report rather
	 * than a fresh draft. */
	savedReportSeed?: SavedReportRef;
	/** Whether the route carried a report id, i.e. this report already exists in storage — passed
	 * through independent of `savedReportSeed`, which can be absent even for a genuinely stored
	 * report (see `SavedReportBuilder`). Defaults to false, matching the chat-intent path below,
	 * which never opens a stored report. */
	openedFromStorage?: boolean;
	/** Defaults to editor: the only way to reach the builder without a stored report is the chat
	 * intent, and the person who just generated a draft is its author. */
	role?: BuilderRole;
	/** Announced once, when this builder first turns its payload into a stored report. */
	onIntentSaved?: (savedReportId: string) => void;
}

/** Mounts the provider + surface once the page guards have passed. Kept as its own component so the
 * taxonomy fetch hook runs unconditionally (never behind the page's early returns), and so the
 * builder resolves its own taxonomy rather than depending on the ephemeral chat handoff — a
 * missing/slow/failed handoff no longer hides the conditions editor. */
function ReportBuilderView({
	initialConfig,
	userId,
	taxonomySeed,
	dataSource,
	savedReportSeed,
	openedFromStorage = false,
	role = 'editor',
	onIntentSaved,
}: ReportBuilderViewProps): JSX.Element {
	// The definition is passed whole, not just its reportType: an agent fallback definition can
	// carry projectStatus.* fields with no reportType, and only the field root reveals its grain.
	const taxonomy = useReportTaxonomy(grainOf(initialConfig.definition), taxonomySeed);
	return (
		// Key by draftId so a refined create_report_intent (new draftId) remounts the provider with
		// the new definition. The provider seeds definition/grouping/tableState once on mount with no
		// reset-on-payload, so without a changing key a refinement would keep the previous draft.
		// Falls back to a stable key for non-intent opens.
		<ReportBuilderProvider
			key={initialConfig.draftId ?? 'report-builder'}
			payload={initialConfig}
			role={role}
			dataSource={dataSource}
			userId={userId}
			taxonomy={taxonomy}
			savedReportSeed={savedReportSeed}
			openedFromStorage={openedFromStorage}
			onIntentSaved={onIntentSaved}
		>
			<ReportBuilderSurface />
		</ReportBuilderProvider>
	);
}

type LoadState =
	| {status: 'loading'}
	| {status: 'loaded'; report: LoadedReport}
	/** `accessDenied` separates the refusal a retry cannot clear from an ordinary load failure. The
	 * two render different copy, so the distinction has to survive into state rather than being
	 * discarded at the rejection site. */
	| {status: 'failed'; accessDenied: boolean; refusalReason?: ReportRefusalReason};

/** Re-opens a report that was saved from the builder, loading its definition by id. The report intent
 * the chat hands over is ephemeral, so a saved report has no router state to restore from and its
 * definition is read back from storage instead. */
function SavedReportBuilder({savedReportId, dataSource}: {savedReportId: string; dataSource: ReportDataSource}): JSX.Element {
	const {formatMessage} = useIntl();
	const [state, setState] = useState<LoadState>({status: 'loading'});

	// The entry this report already occupies in the Reports list, so a save updates it rather than
	// creating a duplicate and publishing that. Only seeded when the stored record carried a real
	// `privateAccess`: an update overwrites that field unconditionally, so a guessed value would flip the
	// report between private and shared, and a duplicate is the lesser of the two harms.
	//
	// Memoised, and declared above the early returns so the hook order never changes: the provider keys
	// its mount-time lock-status read and its socket subscription on this object's identity, so a fresh
	// object per render would re-query the lock and re-subscribe on every parent re-render.
	const loadedReport = state.status === 'loaded' ? state.report : null;
	const savedReportSeed = useMemo<SavedReportRef | undefined>(
		() =>
			loadedReport === null || loadedReport.privateAccess === null
				? undefined
				: {id: savedReportId, privateAccess: loadedReport.privateAccess},
		[savedReportId, loadedReport]
	);

	useEffect(() => {
		let cancelled = false;
		setState({status: 'loading'});
		void dataSource.loadDefinition(savedReportId).then(
			stored => {
				if (cancelled) {
					return;
				}
				setState({status: 'loaded', report: stored});
			},
			error => {
				if (!cancelled) {
					// The definition load is the path a viewer opening someone else's report takes, so
					// this is where a refusal has to keep its status. Anything without one — a network
					// failure, a parse error — reads as retryable, which is the honest default.
					setState({
						status: 'failed',
						accessDenied: isReportDataAccessDenied(error),
						refusalReason: error instanceof ReportDataFetchError ? error.reason : undefined,
					});
				}
			}
		);
		return () => {
			cancelled = true;
		};
	}, [savedReportId, dataSource]);

	// The app's standard loading overlay — the same one the Reports list shows while its query resolves,
	// so opening a report looks like opening any other page. This used to render the error panel: a load
	// that has not finished is not a load that failed, and showing an error affordance for ordinary
	// waiting told the reader something had gone wrong when nothing had. The failure branch below still
	// uses ErrorPanel, which is its job.
	if (state.status === 'loading') {
		// Wrapped for the hook alone: UploadingOverlay renders a bare div with no data-cy, no role and no
		// accessible name, so without this the page-level wait is unassertable. Distinct from the row
		// fetch's hook -- one name covering both would let a "not.exist" pass while the other is running.
		return (
			<TestHook data-cy={'ai-report-builder-page-loading'}>
				<UploadingOverlay />
			</TestHook>
		);
	}

	if (state.status === 'failed') {
		// Deliberately NOT the row-fetch refusal's words. A refusal here is about the report itself - it
		// was not shared with this reader, or its type needs a permission they lack - so it names who
		// can help. The row-fetch refusal is about the data behind a report the reader CAN open, and
		// keeps its own wording. A 403 with no readable reason is the common case, not shared.
		const prefix = !state.accessDenied
			? null
			: state.refusalReason === 'REPORT_TYPE_PERMISSION'
			? 'ai_report_builder.error.type_permission'
			: 'ai_report_builder.error.not_shared';
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				{/* An h1: this panel is the whole page when the report cannot be opened. */}
				<Heading size={'3'} as={'h1'}>
					{formatMessage({id: prefix ? `${prefix}.title` : 'ai_report_builder.error_title'})}
				</Heading>
				<Text>{formatMessage({id: prefix ? `${prefix}.body` : 'ai_report_builder.error_load_failed'})}</Text>
			</ErrorPanel>
		);
	}

	const {valid, errors} = validateReportDefinition(state.report.definition);
	if (!valid) {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Heading size={'3'} as={'h1'}>
					{formatMessage({id: 'ai_report_builder.error_title'})}
				</Heading>
				<Text>{formatMessage({id: 'ai_report_builder.error_invalid_definition'})}</Text>
				<Text>{errors.join(', ')}</Text>
			</ErrorPanel>
		);
	}

	// A report naming a type this builder does not build has no field vocabulary to edit it with, so
	// the conditions editor renders NOTHING -- which takes the report's existing filter tiles and
	// their delete buttons with it, while the filter itself still runs against the row fetch. That
	// strands a reader with a filter they can neither see nor remove, so say the report cannot be
	// opened here rather than opening a surface that silently drops its own controls.
	if (grainOf(state.report.definition) === null) {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Heading size={'3'} as={'h1'}>
					{formatMessage({id: 'ai_report_builder.error_title'})}
				</Heading>
				<Text>{formatMessage({id: 'ai_report_builder.error_unsupported_report_type'})}</Text>
			</ErrorPanel>
		);
	}

	// Anything short of edit rights is a reader. Failing closed matters more than being generous:
	// showing edit affordances the backend will refuse is worse than hiding ones it would allow.
	const role: BuilderRole =
		state.report.viewerPermission === 'OWNER' || state.report.viewerPermission === 'EDITOR' ? 'editor' : 'reader';

	// Keyed by report id so navigating between two saved reports remounts the provider, which seeds its
	// working state once on mount.
	return (
		<ReportBuilderView
			key={savedReportId}
			initialConfig={{
				definition: state.report.definition,
				draftId: savedReportId,
				prompt: state.report.promptText ?? undefined,
				source: state.report.source,
				// The stored report's lifecycle state, so the banner, the publish date and the Discard action
				// describe the report as it actually is. An unrecognised/absent state stays absent here, which
				// the provider reads as UNPUBLISHED — the conservative reading, never a claimed draft.
				draftState: state.report.draftState ?? undefined,
				publishedAt: state.report.publishedAt,
				draftFilterWillRevert: state.report.draftFilterWillRevert,
				manuallyEdited: state.report.manuallyEdited,
			}}
			dataSource={dataSource}
			savedReportSeed={savedReportSeed}
			// This component only ever renders once `dataSource.loadDefinition` has resolved a record
			// for `savedReportId`, so the report unconditionally exists in storage here — regardless of
			// whether `savedReportSeed` above could be populated for it.
			openedFromStorage={true}
			role={role}
		/>
	);
}

/** The AI report builder rendered as a routed view (path `/report-builder`), mounted in the app's
 * main content region beside the primary navigation. Reached only from the chat card's "View", which
 * navigates here with the report intent in location state. Security-critical gate: redirects to `/`
 * unless `canOpenReportBuilder()` passes AND an intent is present in location state. Guards a
 * malformed `initialConfig.definition` (e.g. an upstream agent response that fails schema
 * validation) by rendering an error block instead of crashing the surface/provider. */
export function ReportBuilderPage({dataSource = httpReportDataSource}: ReportBuilderPageProps): JSX.Element {
	const {formatMessage} = useIntl();
	const location = useLocation<ReportBuilderLocationState>();
	const {reportId: reportIdParam} = useParams<{reportId?: string}>();
	const state = location.state;
	// Optional: the drawer's provider wraps the whole app in production, but this page is also mounted
	// standalone in tests/Storybook, where there is no chat transcript to write back to.
	const agentDrawer = useAgentDrawerContextOptional();
	const intentMessageId = state?.intentMessageId;

	// The provider knows the new report's id but not which chat message asked for it; the page knows
	// both, so it closes over the message id here rather than widening the provider's callback.
	const handleIntentSaved = React.useCallback(
		(savedReportId: string) => {
			if (intentMessageId) {
				agentDrawer?.recordReportIntentSaved(intentMessageId, savedReportId);
			}
		},
		[intentMessageId, agentDrawer]
	);

	// A task modal opened in place still routes its own internal links (breadcrumb parent, subtask
	// rows, dependencies) through the app's URL helper, which appends `/T{id}` to whatever path is
	// showing. On this page that lands in the `:reportId` slot. Reading it as a report id would ask
	// storage for a report by that name and, for a draft that only exists in router state, drop the
	// report on screen entirely. A task segment is not a report id, so it is ignored here and the
	// page keeps rendering whatever it was already showing.
	const reportId = reportIdParam !== undefined && /^T\d+$/.test(reportIdParam) ? undefined : reportIdParam;

	if (!canOpenReportBuilder()) {
		return <Redirect to="/" />;
	}

	// Re-opening a saved report: the definition comes from storage, not from router state.
	if (reportId) {
		return (
			<TrackedSurface key={reportId} reportId={reportId}>
				<SavedReportBuilder savedReportId={reportId} dataSource={dataSource} />
			</TrackedSurface>
		);
	}

	if (!state || !state.initialConfig) {
		return <Redirect to="/" />;
	}

	const {initialConfig, userId, taxonomy} = state;
	const {valid, errors} = validateReportDefinition(initialConfig.definition);

	if (!valid) {
		return (
			<ErrorPanel data-cy="ai-report-builder-error-panel">
				<Heading size={'3'} as={'h1'}>
					{formatMessage({id: 'ai_report_builder.error_title'})}
				</Heading>
				<Text>{formatMessage({id: 'ai_report_builder.error_invalid_definition'})}</Text>
				<Text>{errors.join(', ')}</Text>
			</ErrorPanel>
		);
	}

	return (
		<TrackedSurface key={initialConfig.draftId ?? 'draft'}>
			<ReportBuilderView
				initialConfig={initialConfig}
				userId={userId}
				taxonomySeed={taxonomy}
				dataSource={dataSource}
				onIntentSaved={handleIntentSaved}
			/>
		</TrackedSurface>
	);
}

export default ReportBuilderPage;
